import * as Sentry from "@sentry/react";
import { storage } from "./storage";
import { timer } from "utils/constants";
import { NEW_DATA_EVENT } from "App";

const TYPE = "GAME";
const GAME_MOUNT = "GAME_MOUNT";
const GAME_DATA_SET = "GAME_DATA_SET";
const GAME_DATA_SAVE = "GAME_DATA_SAVE";
const GAME_END = "GAME_END";
const GAME_CREATE_MASTER_SESSION = "GAME_CREATE_MASTER_SESSION";

/**
 * Get the parent app origin
 * @returns {string}
 */
const getParentOrigin = () => {
  const parentOrigin =
    window.location !== window.parent.location
      ? document.referrer
      : document.location.href;
  return parentOrigin.endsWith("/") ? parentOrigin.slice(0, -1) : parentOrigin;
};

/**
 * Event handler for catching postMessage events from parent app
 * @param {*} event
 * @returns
 */
const messageEventHandler = (event) => {
  // Ignore other message events which are not from assessment center origin
  if (event.origin !== getParentOrigin()) {
    return;
  }
  console.log("🚀 ~ ShoveBerry Event Handler ::", event);
  // Saving authToken & gameplayId from Assessment centre
  const { user, gameId, gameLevelData, gameDetails = {} } = event.data;
  let gameOrgDetails = {
    mediaUrl: gameDetails?.mediaUrl,
    organizationName: gameDetails?.organizationName,
    gameName: gameDetails?.gameName,
  };
  // Setting this way since some extensions override iframe and send their data keys in message
  // So only when data keys relevant to code arrive, populate data. 
  gameOrgDetails && storage.set.gameDetails(gameOrgDetails);
  gameId && storage.set.gameId(gameId);
  gameLevelData && storage.set.levelData(gameLevelData ?? {});
  timer && storage.set.timeRemaining(timer * 60);

  if (gameLevelData?.levelData?.length) {
    const { levelData } = gameLevelData;
    let timeElapsed = 0;
    levelData.forEach((level) => {
      timeElapsed += level.totalTime;
    });
    const timeRemaining = timer * 60 - timeElapsed;
    storage.set.timeRemaining(timeRemaining > 0 ? timeRemaining : 0);
  }

  // Setting user context for sentry
  try {
    Sentry.setUser(user);
  } catch (error) {
    console.error(error);
    Sentry.captureException(error);
  }
  //custom storage event dispatch for resuming game
  window.dispatchEvent(new Event(NEW_DATA_EVENT));
  // sending start game event
  sendGameDataSetMessage();
};

/**
 * Send message to the parent app (Assessment centre)
 * @param {any} message message to be sent
 * @param {object} options optional params
 * @return
 */
const sendMessageToGamecenter = (message, options) => {
  const { addGameName } = options ?? {};

  const url = getParentOrigin();
  const data = addGameName
    ? { ...message, game: process.env.REACT_APP_GAME_NAME, type: TYPE }
    : { ...message, type: TYPE };
  window.parent.postMessage(data, url);
};

/**
 * Event for game mount
 */
const sendGameLoadMessage = () => {
  const message = { status: GAME_MOUNT };
  sendMessageToGamecenter(message);
};

/**
 * Event to be sent when all game data has been set
 * and game can be played
 */
const sendGameDataSetMessage = () => {
  const message = { status: GAME_DATA_SET };
  sendMessageToGamecenter(message);
};

/**
 * Event for gameLevelData save, only data
 * is sent for this game.
 */
const sendGameDataSaveMessage = (gameLevelData) => {
  const message = { status: GAME_DATA_SAVE, gameLevelData };
  sendMessageToGamecenter(message);
};

/**
 * Event for game end, send gameId to parent app
 */
const sendGameEndMessage = () => {
  const message = { status: GAME_END, gameId: storage.get.gameId() };
  sendMessageToGamecenter(message);
};

const createGameMasterSession = () => {
  const message = {
    status: GAME_CREATE_MASTER_SESSION
  };
  sendMessageToGamecenter(message);
};

export const GC = {
  messageEventHandler,
  sendGameLoadMessage,
  sendGameDataSaveMessage,
  sendGameEndMessage,
  createGameMasterSession
};
